import React, { useEffect, useState, useRef } from "react";
import "./styles.css";

import axios from "axios";
import { toast } from "react-toastify";

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Grid,
  List,
  ListItem,
  IconButton,
  CircularProgress,
  Tooltip,
  ListSubheader,
} from "@mui/material";
import ReactPlayer from "react-player";

import InfoIcon from "@mui/icons-material/Info";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from '@mui/icons-material/Delete';
import { deleteVideo, getTopResultsForVideo } from "../../services/api.service";
import { normaliseVideoScore } from "../../helpers/video.helper";
import { PageNumberSelector } from "./PageNumberSelector";

const mapProbabilityToColor = (probability) => {
  if (probability > 95) {
    return "#D84141";
  } else if (probability >= 50) {
    return "#FF7744";
  } else {
    return "#66BB66";
  }
};

const REQUIREMENTS = {
  "Highly Likely Fake": "Falsehood score above 80%",
  "Likely Fake": "Falsehood score above 50%",
  "Possibly Fake": "Falsehood score above 40%",
  "Likely Real": "Falsehood score above 30%",
  "Highly Likely Real": "Falsehood score below 30%",
};

const RECOMMENDED_ACTIONS = {
  "Highly Likely Fake":
    "Immediate human review, likely takedown depending on content",
  "Likely Fake":
    "Medium to Low priority queue for human review depending on surrounding context",
  "Possibly Fake":
    "Low priority queue or no human review depending on surrounding context",
  "Likely Real": "No human review depending on surrounding context",
  "Highly Likely Real": "No human review",
};

const generateActionRecommendationTooltip = (actionClassification) => {
  return (
    <>
      <b>Classification</b>
      <div>{actionClassification}</div>
      <b>Requirements</b>
      <div>{REQUIREMENTS[actionClassification]}</div>
      <b>Recommended actions</b>
      <div>{RECOMMENDED_ACTIONS[actionClassification]}</div>
    </>
  );
};

export const FileHistory = ({
  files,
  setFiles,
  setSearchedFiles,
  filesLoading,
  selectedModality,
  currentPage,
  setPage,
  continuationToken
}) => {
  const playerRef = useRef(null);
  const [currentFile, setCurrentFile] = useState();
  const [topTrajectories, setTopTrajectories] = useState({});

  const notifyDeleteSuccess = () => toast.success("File deleted successfully!");
  const notifyDeleteFailure = () => toast.error("File delete failed!");

  const downloadResults = (file, md5) => {
    const currentModality = selectedModality
      ? selectedModality
      : file.modalities[0];
    axios({
      url:
        "https://app.deepidentify.ai/api/file/results/" + currentModality + "/" + md5,
      method: "GET",
      responseType: "blob",
    }).then((response) => {
      const href = URL.createObjectURL(response.data);

      const link = document.createElement("a");
      link.href = href;
      link.setAttribute(
        "download",
        file.filename.replace(/\.[^/.]+$/, "") + ".zip"
      ); 
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    });
  };

  const downloadVisualisation = (file, md5) => {
    axios({
      url: "https://app.deepidentify.ai/api/download/visualisation/" + md5,
      method: "GET",
      responseType: "blob",
    }).then((response) => {
      const href = URL.createObjectURL(response.data);

      const link = document.createElement("a");
      link.href = href;
      link.setAttribute(
        "download",
        file.filename.replace(/\.[^/.]+$/, "") + '_visualisations.gz'
      ); 
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    });
  };

  const getAndSetTopResultsForVideo = async () => {
    const currentFileModalities = files.filter(
      (file) => file.md5 == currentFile
    )[0].modalities;

    currentFileModalities.map(async (modality) => {
      const response = await getTopResultsForVideo(modality, currentFile);

      if (response.status == 200 || response.status == 201) {
        setTopTrajectories((prevTopTrajectories) => ({
          ...prevTopTrajectories,
          [modality]: response.data,
        }));
      } else {
        toast.error("Getting fake parts of the video failed!");
      }
    });
  };

  const getTopActionScore = (file) => {
    const action_prediction_values = Object.values(
      file?.action_prediction ?? {}
    );

    if (action_prediction_values.includes("Highly Likely Fake"))
      return "Highly Likely Fake";
    else if (action_prediction_values.includes("Likely Fake"))
      return "Likely Fake";
    else if (action_prediction_values.includes("Possibly Fake"))
      return "Possibly Fake";
    else if (action_prediction_values.includes("Likely Real"))
      return "Likely Real";
    else if (action_prediction_values.includes("Highly Likely Real"))
      return "Highly Likely Real";

    return "-";
  };

  const deleteVideoAndSetFiles = async (id) => {
    try {
      if (currentFile) {
        setCurrentFile(null);
      }
      setFiles(prevFiles => prevFiles.filter(file => file.md5 != id));
      setSearchedFiles(prevFiles => {
        if (prevFiles) {
          return prevFiles.filter(file => file.md5 != id)
        }

        return null;
      });

      await deleteVideo(id);

      notifyDeleteSuccess();
    } catch {
      notifyDeleteFailure();
    }
  }

  useEffect(() => {
    if (selectedModality == "image" || currentFile == null) return;

    setTopTrajectories({});

    getAndSetTopResultsForVideo();
  }, [currentFile]);

  useEffect(() => {
    setCurrentFile(null);
  }, [selectedModality]);

  return (
    files && (
      <div className="historyContainer">
        <div className="historyHeader">
          <p className="headerTitle">File title</p>
          <p className="headerTitle">File duration</p>
          <p className="headerTitle">Modalities</p>
          <p className="headerTitle">Falsehood</p>
          <p className="headerTitle">Uploaded by</p>
        </div>

        {filesLoading ? (
          <div className="filesLoading">
            <CircularProgress sx={{ color: "white" }} />
          </div>
        ) : (
          files.map((file, index) => {
            if (file.modalities.includes("image")) {
              const currentFileScore = Math.round(
                parseFloat(file.score["image"]),
                2
              );
              const imageFilepath =
                "https://static.deepidentify.ai/" + encodeURIComponent(file.filename);
              const heatmapFilepath =
                "https://app.deepidentify.ai/api/" +
                (file?.heatmap ?? "").replace("s3://sagemaker-slowfast-data-2/", "");

              return (
                <Accordion
                  key={`${file.filename}_${index}`}
                  square={false}
                  sx={{
                    borderRadius: "30px",
                    marginBottom: "1%",
                    "&:before": {
                      display: "none",
                      height: 0,
                    },
                  }}
                  expanded={currentFile == file.md5}
                  onChange={() =>
                    setCurrentFile((currentFile) =>
                      currentFile == file.md5 ? null : file.md5
                    )
                  }
                  className="history-items"
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <p className="summaryText">{file.filename}</p>
                    <p className="summaryText">-</p>
                    <p className="summaryText">{file.modalities.join(", ")}</p>
                    <p className="summaryText">{currentFileScore}%</p>
                    <p className="summaryText">{file.user_name}</p>
                    <p className="deleteIcon">
                      <IconButton onClick={(e) => {
                        e.stopPropagation();
                        e.nativeEvent.stopImmediatePropagation();
                        deleteVideoAndSetFiles(file.md5);
                      }}>
                        <DeleteIcon />
                      </IconButton>
                    </p>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      backgroundColor: "#151515",
                    }}
                  >
                    <div className="historyAccordionContainer">
                      <div className="resultsContainer">
                        <div className="textContainer">
                          <div className="headerText">
                            This is the result of your video.
                          </div>
                          <div className="subText">
                            The probability of falsehood of your video is:
                          </div>
                        </div>
                        <div
                          className="resultScore"
                          style={{
                            color: mapProbabilityToColor(currentFileScore),
                          }}
                        >
                          {currentFileScore}%
                        </div>
                      </div>
                    </div>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <p className="fragmentText">Original image</p>
                        {currentFile == file.md5 ? (
                          <img src={imageFilepath} width="90%" />
                        ) : null}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <p className="fragmentText">Detection heatmap</p>
                        {currentFile == file.md5 ? (
                          <img src={heatmapFilepath} width="90%" />
                        ) : null}
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );
            } else {
              const currentFileDuration = new Date(file.size["seconds"] * 1000)
                .toISOString()
                .slice(11, 19);
              const modality = selectedModality
                ? selectedModality
                : file.modalities[0];
              const rawScore = file.score[modality];

              const currentFileScore = Math.round(rawScore, 2);
              const videoFilepath =
                "https://static.deepidentify.ai/" +
                encodeURIComponent(file.filename);
              const topActionScore = getTopActionScore(file);
              const timestamp = new Date(file.created_at);

              return (
                <Accordion
                  key={`${file.filename}_${index}`}
                  square={false}
                  sx={{
                    borderRadius: "16px",
                    marginBottom: "1%",
                    border: "none !important",
                    boxShadow: "none !important",
                  }}
                  expanded={currentFile == file.md5}
                  onChange={() =>
                    setCurrentFile((currentFile) =>
                      currentFile == file.md5 ? null : file.md5
                    )
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <p className="summaryText">{file.filename}</p>
                    <p className="summaryText">{currentFileDuration}</p>
                    <p className="summaryText">{file.modalities.join(", ")}</p>
                    <p className="summaryText">{currentFileScore}%</p>
                    <p className="summaryText">{file.user_name}</p>
                    <p className="deleteIcon">
                      <IconButton onClick={(e) => {
                        e.stopPropagation();
                        e.nativeEvent.stopImmediatePropagation();
                        deleteVideoAndSetFiles(file.md5);
                      }}>
                        <DeleteIcon />
                      </IconButton>
                    </p>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      backgroundColor: "#151515",
                    }}
                  >
                    <div className="historyAccordionContainer">
                      <div className="resultsContainer">
                        <div className="textContainer">
                          <div className="headerText">
                            This is the result of your video
                          </div>
                          <div className="subText">
                            The probability of falsehood of your video is:{" "}
                            {topActionScore}
                            {topActionScore != "-" ? (
                              <Tooltip
                                title={generateActionRecommendationTooltip(
                                  topActionScore
                                )}
                                style={{ marginTop: "20px" }}
                              >
                                <InfoIcon />
                              </Tooltip>
                            ) : null}
                          </div>
                        </div>
                        <div className="rightSideContainer">
                          {file.modalities &&
                          file.modalities.includes("audio") &&
                          file.modalities.includes("video") ? (
                            <>
                              <div
                                className="resultScore"
                                style={{
                                  color: mapProbabilityToColor(
                                    Math.round(file.score["audio"], 2)
                                  ),
                                }}
                              >
                                <span className="resultScoreLegend">
                                  Audio probability: <br />
                                </span>{" "}
                                {Math.round(file.score["audio"], 2)}%
                              </div>
                              <div
                                className="resultScore"
                                style={{
                                  color: mapProbabilityToColor(
                                    Math.round(file.score["video"], 2)
                                  ),
                                }}
                              >
                                <span className="resultScoreLegend">
                                  Video probability: <br />
                                </span>{" "}
                                {Math.round(file.score["video"], 2)}%
                              </div>
                            </>
                          ) : (
                            <div
                              className="resultScore"
                              style={{
                                color: mapProbabilityToColor(currentFileScore),
                              }}
                            >
                              {currentFileScore}%
                            </div>
                          )}
                        </div>
                      </div>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <div className="videoAndTextContainers">
                            <div className="videoPlayerContainer">
                              {currentFile == file.md5 ? (
                                <ReactPlayer
                                  url={videoFilepath}
                                  className="main-video"
                                  controls
                                  width="90%"
                                />
                              ) : null}
                              Uploaded date: {timestamp.toLocaleDateString()} |
                              Uploaded time: {timestamp.toLocaleTimeString()}
                            </div>
                            <div className="buttonContainer">
                              <p className="descriptionText">
                                Our analysis have detected a possibility of your
                                video being fake.
                              </p>
                              <p className="descriptionText">
                                Download the detailed results here
                              </p>
                              <Button
                                onClick={() => {
                                  downloadResults(file, file.md5);
                                }}
                                variant="contained"
                                endIcon={<DownloadIcon />}
                                sx={{
                                  backgroundColor: "#4455BB",
                                  color: "#FFFFFF",
                                  borderRadius: "16px 0px 16px 0px",
                                  marginBottom: "15px",
                                  padding: "3% 1.25%",
                                  width: "10vw",
                                  minWidth: "50%",
                                  "&:hover": {
                                    backgroundColor: "#5C70E0",
                                  },
                                }}
                                className="download-results"
                              >
                                Download results
                              </Button>
                              <Button
                                disabled={file.status != "PROCESSED" || file.run_visualisation == false}
                                onClick={() => {
                                  downloadVisualisation(file, file.md5);
                                }}
                                variant="contained"
                                endIcon={<DownloadIcon />}
                                sx={{
                                  backgroundColor: "#4455BB",
                                  color: "#FFFFFF",
                                  borderRadius: "16px 0px 16px 0px",
                                  padding: "3% 1.25%",
                                  width: "10vw",
                                  "&:hover": {
                                    backgroundColor: "#5C70E0",
                                  },
                                  minWidth: "50%",
                                }}
                                className="download-visualisation"
                              >
                                Download visualisation
                              </Button>
                            </div>
                          </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <p className="fragmentText">Fragment (20)</p>
                          {currentFile == file.md5 ? (
                            <ReactPlayer
                              ref={playerRef}
                              url={videoFilepath}
                              className="sub-video"
                              width="90%"
                              controls
                            />
                          ) : null}
                          <div
                            style={{ display: "flex", flexDirection: "row" }}
                          >
                            {Object.keys(topTrajectories).map((modality) => {
                              const titleModality =
                                modality.charAt(0).toUpperCase() +
                                modality.slice(1);

                              return (
                                <Grid item xs={12} md={6} key={modality}>
                                  <List>
                                    <ListSubheader>
                                      {titleModality}
                                    </ListSubheader>
                                    {topTrajectories[modality].map(
                                      (trajectory, index) => {
                                        const startTimeFormatted = new Date(
                                          trajectory.start_time * 1000
                                        )
                                          .toISOString()
                                          .substring(14, 19);
                                        const endTimeFormatted = new Date(
                                          trajectory.end_time * 1000
                                        )
                                          .toISOString()
                                          .substring(14, 19);
                                        return (
                                          <ListItem
                                            className="trajectory"
                                            key={`trajectory_${index}`}
                                          >
                                            <div className="trajectoryIndexText">
                                              {index + 1}
                                            </div>
                                            <IconButton
                                              onClick={() =>
                                                playerRef.current.seekTo(
                                                  Math.floor(
                                                    trajectory.start_time
                                                  )
                                                )
                                              }
                                            >
                                              <PlayArrowIcon fontSize="large" />
                                            </IconButton>
                                            <div className="trajectoryTimeText">
                                              {startTimeFormatted} -{" "}
                                              {endTimeFormatted}
                                            </div>
                                            <div className="trajectoryResultText">
                                              <div
                                                style={{
                                                  color: mapProbabilityToColor(
                                                    trajectory[modality]
                                                  ),
                                                }}
                                              >
                                                {Math.round(
                                                  trajectory[modality]
                                                ).toString() + "%"}
                                              </div>
                                            </div>
                                          </ListItem>
                                        );
                                      }
                                    )}
                                  </List>
                                </Grid>
                              );
                            })}
                          </div>
                        </Grid>
                      </Grid>
                    </div>
                  </AccordionDetails>
                </Accordion>
              );
            }
          })
        )}

        <div className="pagination">
          <PageNumberSelector
            currentPage={currentPage}
            setPage={setPage}
            continuationToken={continuationToken}
          />
        </div>
      </div>
    )
  );
};
